import {
  Required,
  IsString,
  IsNumber,
  IsBoolean,
  DataValidator,
  transformKeys,
  IsArray,
  ValidateNested,
} from "@/utils/type-vaildator";
import api, { apiV2 } from "./api";

interface AudioShowParams {
  condition1: string;
  condition2: {
    [key: string]: string[];
  };
  condition3:
  | "all_voices"
  | "starred_voices"
  | "recently_used"
  | "default_voices"
  | "my_voices";
  page: number;
  page_size: number;
  user_id?: string;
}

export interface AudioShowProps {
  name: string;
  condition:
  | "all_voices"
  | "starred_voices"
  | "recently_used"
  | "default_voices"
  | "my_voices";
  filters: {
    [key: string]: string[];
  };
  page: number;
  size: number;
  userId?: string;
}

// 原始接口
interface AudioShowResponse {
  page_size: number;
  total: number;
  total_pages: number;
  current_page: number;
  audios: {
    id: string;
    type: number;
    voice_tts_id: string;
    model_name: string;
    script: string;
    name: string;
    age_group: string;
    language: string;
    gender: string;
    emotion: string;
    speed: number;
    audio_url: string;
    duration: number;
    is_starred: boolean;
    is_selected: boolean;
    sort_order: number;
    use_count: number;
    source: string;
  }[];
}

interface AudioShowMyVoiceResponse {
  page: number;
  pageSize: number;
  total: number;
  templates: {
    created_at: string;
    description: string;
    id: string;
    input_text: string;
    is_starred: boolean;
    name: string;
    process_status: number;
    provider: string;
    sample_url: string;
    task_id: string;
    updated_at: string;
    user_id: string;
    voice_tts_id: string;
    is_my_voice: boolean;
  }[];
}

// 嵌套对象类 - 处理每个音频项
class Audio {
  @Required("ID cannot be empty")
  @IsString("ID must be a string")
  id: string;

  @Required("Voice TTS ID cannot be empty")
  @IsString("Voice TTS ID must be a string")
  voiceTtsId: string;

  @Required("Model name cannot be empty")
  @IsString("Model name must be a string")
  modelName: string;

  @Required("Name cannot be empty")
  @IsString("Name must be a string")
  name: string;

  @Required("Age group cannot be empty")
  @IsString("Age group must be a string")
  ageGroup: string;

  @Required("Language cannot be empty")
  @IsString("Language must be a string")
  language: string;

  @Required("Gender cannot be empty")
  @IsString("Gender must be a string")
  gender: string;

  // @Required("Emotion cannot be empty")
  // @IsString("Emotion must be a string")
  // emotion: string;

  @Required("Speed cannot be empty")
  @IsNumber("Speed must be a number")
  speed: number;

  @Required("Audio URL cannot be empty")
  @IsString("Audio URL must be a string")
  audioUrl: string;

  @Required("Is starred cannot be empty")
  @IsBoolean("Is starred must be a boolean")
  isStarred: boolean;

  source?: string;

  createdAt?: string;
}

// 主数据类
export class AudioShowData {
  @Required("Page size cannot be empty")
  @IsNumber("Page size must be a number")
  pageSize: number;

  @Required("Total cannot be empty")
  @IsNumber("Total must be a number")
  total: number;

  @Required("Current page cannot be empty")
  @IsNumber("Current page must be a number")
  currentPage: number;

  @Required("Audios cannot be empty")
  @IsArray("Audios must be an array")
  @ValidateNested(Audio)
  audios: Audio[];
}

export class MyVoiceAudio {
  @Required("Created at cannot be empty")
  @IsString("Created at must be a string")
  createdAt: string;

  @Required("Id cannot be empty")
  @IsString("Id must be a string")
  id: string;

  @Required("IsStarred cannot be empty")
  @IsBoolean("IsStarred must be a boolean")
  isStarred: boolean;

  @Required("Name cannot be empty")
  @IsString("Name must be a string")
  name: string;

  @Required("ProcessStatus cannot be empty")
  @IsNumber("ProcessStatus must be a number")
  processStatus: number;

  @Required("Provider cannot be empty")
  @IsString("Provider must be a string")
  provider: string;

  @Required("SampleUrl cannot be empty")
  @IsString("SampleUrl must be a string")
  sampleUrl: string;

  @Required("UserId cannot be empty")
  @IsString("UserId must be a string")
  userId: string;

  @Required("VoiceTtsId cannot be empty")
  @IsString("VoiceTtsId must be a string")
  voiceTtsId: string;
}

export class AudioShowMyVoiceData {
  @Required("Page cannot be empty")
  @IsNumber("Page must be a number")
  page: number;

  @Required("Page size cannot be empty")
  @IsNumber("Page size must be a number")
  pageSize: number;

  @Required("Total cannot be empty")
  @IsNumber("Total must be a number")
  total: number;

  // @Required("Templates cannot be empty")
  @IsArray("Templates must be an array")
  @ValidateNested(MyVoiceAudio)
  templates: MyVoiceAudio[];
}

const transformAndValidateAudioShow = (
  response: AudioShowResponse | AudioShowMyVoiceResponse
): AudioShowData => {
  const transformedData = transformKeys(response);

  try {
    if (!transformedData.audios) {
      const data = DataValidator.validateOrThrow(transformedData, AudioShowMyVoiceData);
      return {
        currentPage: data.page,
        pageSize: data.pageSize,
        total: data.total,
        audios: data.templates ? data.templates.map((template) => ({
          id: template.id,
          voiceTtsId: template.voiceTtsId,
          modelName: template.provider,
          name: template.name,
          audioUrl: template.sampleUrl,
          isStarred: template.isStarred,
          ageGroup: "",
          language: "",
          gender: "",
          speed: 1,
          createdAt: template.createdAt,
        })) : [],
      }
    }
    return DataValidator.validateOrThrow(transformedData, AudioShowData);
  } catch (error) {
    console.error("Audio show data validation failed:", error.message);
    throw error;
  }
};

const URL = "/audio/show";

// 获取音频列表
export const audioShow = async ({
  name,
  condition,
  filters,
  page,
  size,
  userId,
}: AudioShowProps): Promise<AudioShowData> => {
  const params: AudioShowParams = {
    condition1: name,
    condition2: filters,
    condition3: condition,
    page: page,
    page_size: size,
    user_id: userId,
  };
  const response = await apiV2.post<AudioShowResponse | AudioShowMyVoiceResponse>(URL, params);
  const validatedData = transformAndValidateAudioShow(response.data);
  return validatedData;
};
